import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../../PermissionsProvider";
import { FinetuneRunService } from "../../../../services/FinetuneRunService";
import { TrainingMethod } from "../../../../model_gen/finetuning";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { SettingsIcon } from "lucide-react";
import ReviewField from "./ReviewField";
import PythonCodeBlock from "../../../shared/PythonCodeBlock";

interface ReviewFinetuneSettingsCardProps {
  trainingMethod?: TrainingMethod;
  dpoBeta?: number;
  grpoNumGenerations?: number;
  grpoMaxCompletionLength?: number;
  grpoRewardFunction?: string;
  grpoTemperature?: number;
  grpoBeta?: number;
  grpoStepsPerGeneration?: number;
  displayName: string;
  description?: string;
  baseModel: string;
  baseFinetuneRunId?: number | null;
  fourbit: boolean;
  warmupSteps?: number;
  warmupRatio: number;
  learningRate: number;
  perDeviceTrainBatchSize: number;
  gradientAccumulationSteps: number;
  trainOnResponsesOnly: boolean;
  lrSchedulerType?: string;
  trainSplit: number;
  alpha?: number;
  rank?: number;
  epochs?: number;
  maxSteps?: number;
  weightDecay?: number;
  dropout?: number;
  rslora?: boolean;
  numGpus?: number;
}

const ReviewFinetuneSettingsCard: React.FC<ReviewFinetuneSettingsCardProps> = ({
  trainingMethod = "SFT",
  dpoBeta,
  grpoNumGenerations,
  grpoMaxCompletionLength,
  grpoRewardFunction,
  grpoTemperature,
  grpoBeta,
  grpoStepsPerGeneration,
  displayName,
  description,
  baseModel,
  baseFinetuneRunId,
  fourbit,
  warmupSteps,
  warmupRatio,
  learningRate,
  perDeviceTrainBatchSize,
  gradientAccumulationSteps,
  trainOnResponsesOnly,
  lrSchedulerType,
  trainSplit,
  alpha,
  rank,
  epochs,
  maxSteps,
  weightDecay,
  dropout,
  rslora,
  numGpus = 1,
}) => {
  const { apiKey } = usePermissions();
  const [baseFinetunedModelName, setBaseFinetunedModelName] = useState<string>("");
  const [baseFinetunedModelLoading, setBaseFinetunedModelLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!baseFinetuneRunId) {
      setBaseFinetunedModelName("");
      return;
    }
    setBaseFinetunedModelLoading(true);
    const sub = FinetuneRunService.getFinetuneRunOptions(
      0,
      1,
      setBaseFinetunedModelLoading,
      apiKey,
      undefined,
      undefined,
      undefined,
      undefined,
      baseFinetuneRunId
    ).subscribe(
      (resp: any) => {
        const run = resp?.items?.[0];
        setBaseFinetunedModelName(run?.display_name || "");
      },
      (error: Error) => {
        console.error("Error fetching base finetune run details:", error);
        setBaseFinetunedModelName("");
        setBaseFinetunedModelLoading(false);
      }
    );
    return () => sub.unsubscribe();
  }, [baseFinetuneRunId, apiKey]);

  const resolvedBaseModel = baseFinetuneRunId
    ? baseFinetunedModelLoading
      ? "Loading..."
      : baseFinetunedModelName || "Unknown"
    : baseModel;

  const lrSchedulerLabels: Record<string, string> = {
    linear: "Linear Decay",
    cosine: "Cosine Annealing",
    cosine_with_restarts: "Cosine with Warm Restarts",
    polynomial: "Polynomial Decay",
    constant: "Constant",
    inverse_sqrt: "Inverse Square Root",
  };

  const useMaxSteps = maxSteps !== undefined && maxSteps > 0;

  const fields: Array<{ label: string; value: React.ReactNode; show: boolean }> = [
    { label: "Training Method", value: trainingMethod, show: true },
    { label: "Display Name", value: displayName || "Not specified", show: true },
    { label: "Base Model", value: resolvedBaseModel, show: true },
    { label: "4-bit Quantization", value: fourbit ? "Yes" : "No", show: true },
    { label: "GPUs", value: numGpus && numGpus > 1 ? `${numGpus} (distributed)` : `${numGpus}`, show: true },
    { label: "DPO Beta", value: dpoBeta, show: trainingMethod === "DPO" && dpoBeta !== undefined },
    { label: "Num Generations", value: grpoNumGenerations, show: trainingMethod === "GRPO" && grpoNumGenerations !== undefined },
    { label: "Max Completion Length", value: grpoMaxCompletionLength, show: trainingMethod === "GRPO" && grpoMaxCompletionLength !== undefined },
    { label: "Temperature", value: grpoTemperature, show: trainingMethod === "GRPO" && grpoTemperature !== undefined },
    { label: "KL Penalty (Beta)", value: grpoBeta, show: trainingMethod === "GRPO" && grpoBeta !== undefined },
    { label: "Steps per Generation", value: grpoStepsPerGeneration, show: trainingMethod === "GRPO" && grpoStepsPerGeneration !== undefined },
    { label: "Epochs", value: epochs, show: !useMaxSteps },
    { label: "Max Steps", value: maxSteps, show: useMaxSteps },
    { label: "Learning Rate", value: learningRate, show: true },
    { label: "LR Schedule", value: lrSchedulerLabels[lrSchedulerType || "linear"] || lrSchedulerType, show: true },
    { label: "Warmup Ratio", value: warmupRatio, show: true },
    { label: "Warmup Steps", value: warmupSteps, show: warmupSteps !== undefined && warmupSteps > 0 },
    { label: "Batch Size", value: perDeviceTrainBatchSize, show: true },
    { label: "Gradient Accumulation", value: gradientAccumulationSteps, show: true },
    { label: "Train Split", value: `${(trainSplit * 100).toFixed(0)}%`, show: true },
    { label: "Rank", value: rank, show: rank !== undefined },
    { label: "Alpha", value: alpha, show: alpha !== undefined },
    { label: "Weight Decay", value: weightDecay, show: weightDecay !== undefined },
    { label: "Dropout", value: dropout, show: dropout !== undefined },
    { label: "RSLoRA", value: rslora ? "Yes" : "No", show: rslora !== undefined },
    { label: "Train on Responses Only", value: trainOnResponsesOnly ? "Yes" : "No", show: trainingMethod === "SFT" },
  ];

  return (
    <Card className="border-border">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
          <SettingsIcon className="w-5 h-5 text-maitai-green" />
          Finetune Settings
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fields
            .filter((f) => f.show)
            .map((f) => (
              <ReviewField key={f.label} label={f.label} value={f.value} />
            ))}
        </div>

        {trainingMethod === "GRPO" && grpoRewardFunction && (
          <div className="mt-6 pt-6 border-t border-border space-y-2">
            <ReviewField label="Reward Function" value="Custom" />
            <PythonCodeBlock code={grpoRewardFunction} />
          </div>
        )}

        {description && (
          <div className="mt-6 pt-6 border-t border-border">
            <ReviewField label="Description" value={description} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReviewFinetuneSettingsCard;
